import React, { useMemo, useState, useCallback } from 'react';
import styles from './PracticeList.module.css';
import type { PracticeRecord } from './TrainingDashboard';
import { PracticeForm } from './PracticeForm';
import type { PracticeFormData } from './PracticeForm';
import { BulkImportModal, PRACTICE_COLUMNS } from './BulkImportModal';

export interface PracticeListProps {
  practices: PracticeRecord[];
  showInactive?: boolean;
  onCreatePractice?: (data: Record<string, unknown>) => Promise<void>;
  onUpdatePractice?: (id: string | number, data: Record<string, unknown>) => Promise<void>;
  onDeletePractice?: (id: string | number) => Promise<void>;
  onBulkCreate?: (records: Record<string, unknown>[]) => Promise<unknown>;
}

const CATEGORY_ORDER = ['pricing', 'scope', 'product', 'installation', 'accounting'];

const DOT_CLASS: Record<string, string> = {
  pricing: styles.dotPricing,
  scope: styles.dotScope,
  product: styles.dotProduct,
  installation: styles.dotInstallation,
  accounting: styles.dotAccounting,
};

function priorityClass(priority: string): string {
  switch (priority) {
    case 'critical':
      return styles.priorityCritical;
    case 'standard':
      return styles.priorityStandard;
    case 'nice-to-have':
      return styles.priorityNice;
    default:
      return styles.priorityStandard;
  }
}

function confidenceLevel(confidence: number): string {
  if (confidence >= 0.8) return styles.confidenceHigh;
  if (confidence >= 0.5) return styles.confidenceMedium;
  return styles.confidenceLow;
}

export const PracticeList: React.FC<PracticeListProps> = ({
  practices,
  showInactive = false,
  onCreatePractice,
  onUpdatePractice,
  onDeletePractice,
  onBulkCreate,
}) => {
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingPractice, setEditingPractice] = useState<PracticeRecord | null>(null);
  const [showImport, setShowImport] = useState(false);
  const isAdmin = !!(onCreatePractice || onUpdatePractice || onDeletePractice);

  const filtered = useMemo(() => {
    return showInactive ? practices : practices.filter((p) => p.active);
  }, [practices, showInactive]);

  const grouped = useMemo(() => {
    const groups: Record<string, PracticeRecord[]> = {};
    filtered.forEach((p) => {
      const cat = p.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [filtered]);

  const sortedCategories = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      const idxA = CATEGORY_ORDER.indexOf(a);
      const idxB = CATEGORY_ORDER.indexOf(b);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [grouped]);

  const handleCreate = useCallback(async (data: PracticeFormData) => {
    if (!onCreatePractice) return;
    await onCreatePractice({ ...data });
    setModalMode(null);
  }, [onCreatePractice]);

  const handleEdit = useCallback(async (data: PracticeFormData) => {
    if (!onUpdatePractice || !editingPractice) return;
    await onUpdatePractice(editingPractice.id, { ...data });
    setModalMode(null);
    setEditingPractice(null);
  }, [onUpdatePractice, editingPractice]);

  const handleToggleActive = useCallback(async (practice: PracticeRecord) => {
    if (!onUpdatePractice) return;
    await onUpdatePractice(practice.id, { active: !practice.active });
  }, [onUpdatePractice]);

  const handleDelete = useCallback(async (practice: PracticeRecord) => {
    if (!onDeletePractice) return;
    if (!window.confirm(`Delete practice "${practice.title}"? This cannot be undone.`)) return;
    await onDeletePractice(practice.id);
  }, [onDeletePractice]);

  const openEdit = useCallback((practice: PracticeRecord) => {
    setEditingPractice(practice);
    setModalMode('edit');
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Active Practices</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={styles.count}>{filtered.length} practices</span>
          {isAdmin && (
            <>
              <button className={styles.adminBtn} onClick={() => setModalMode('create')}>
                + New Practice
              </button>
              {onBulkCreate && (
                <button className={styles.adminBtn} onClick={() => setShowImport(true)}>
                  Import CSV/XLSX
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showImport && onBulkCreate && (
        <BulkImportModal
          resourceName="Practices"
          columns={PRACTICE_COLUMNS}
          onImport={onBulkCreate}
          onClose={() => setShowImport(false)}
        />
      )}

      {modalMode && (
        <div className={styles.modalOverlay} onClick={() => { setModalMode(null); setEditingPractice(null); }}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {modalMode === 'edit' ? 'Edit Practice' : 'New Practice'}
            </h3>
            <PracticeForm
              onSubmit={modalMode === 'edit' ? handleEdit : handleCreate}
              onCancel={() => { setModalMode(null); setEditingPractice(null); }}
              initialData={modalMode === 'edit' && editingPractice ? {
                title: editingPractice.title,
                description: editingPractice.description,
                category: editingPractice.category as 'pricing' | 'scope' | 'product' | 'installation' | 'accounting',
                subcategory: editingPractice.subcategory || '',
                priority: editingPractice.priority as 'critical' | 'standard' | 'nice-to-have',
              } : undefined}
            />
          </div>
        </div>
      )}

      <div className={styles.list}>
        {sortedCategories.length === 0 && (
          <div className={styles.emptyState}>No practices recorded yet</div>
        )}

        {sortedCategories.map((category) => (
          <div className={styles.categoryGroup} key={category}>
            <div className={styles.categoryHeader}>
              <span className={`${styles.categoryDot} ${DOT_CLASS[category] || styles.dotPricing}`} />
              <span className={styles.categoryLabel}>{category}</span>
              <span className={styles.categoryCount}>{grouped[category].length}</span>
            </div>

            {grouped[category].map((practice) => (
              <div className={styles.practiceItem} key={practice.id}>
                <div className={styles.practiceTitle}>{practice.title}</div>
                <div className={styles.practiceDescription}>{practice.description}</div>

                <div className={styles.practiceMeta}>
                  <span className={`${styles.metaTag} ${priorityClass(practice.priority)}`}>
                    {practice.priority}
                  </span>
                  {practice.subcategory && (
                    <span className={`${styles.metaTag} ${styles.priorityNice}`}>
                      {practice.subcategory}
                    </span>
                  )}
                </div>

                <div className={styles.practiceStats}>
                  <span className={styles.taughtBy}>
                    Taught by{' '}
                    <span className={styles.taughtByName}>{practice.taughtBy}</span>
                  </span>

                  <div className={styles.stat}>
                    Confidence:
                    <div className={styles.confidenceBar}>
                      <div
                        className={`${styles.confidenceFill} ${confidenceLevel(practice.confidence)}`}
                        style={{ width: `${practice.confidence * 100}%` }}
                      />
                    </div>
                    <span className={styles.statValue}>
                      {Math.round(practice.confidence * 100)}%
                    </span>
                  </div>

                  <div className={styles.stat}>
                    Applied: <span className={styles.statValue}>{practice.timesApplied}</span>
                  </div>
                </div>

                {isAdmin && (
                  <div className={styles.adminActions}>
                    <button className={styles.adminBtnSmall} onClick={() => openEdit(practice)}>
                      Edit
                    </button>
                    <button className={styles.adminBtnSmall} onClick={() => handleToggleActive(practice)}>
                      {practice.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className={`${styles.adminBtnSmall} ${styles.adminBtnDanger}`} onClick={() => handleDelete(practice)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
